import { useEffect, useRef, useState } from 'react';

/**
 * Reveal `target` one chunk at a time so streamed assistant text
 * paints in like a typewriter rather than appearing in 30-char API
 * bursts. The stream itself already produces incremental updates;
 * this hook smooths the visual rhythm so the user sees per-character
 * progress instead of word-by-word pops.
 *
 * Behaviour:
 * - `isStreaming = false` → returns the full text immediately (no
 *   animation for history rows the user is scrolling over).
 * - `isStreaming = true` → reveals chars at ~`tickMs` ms per chunk,
 *   chunk sized so we never fall far behind a fast stream
 *   (`maxCharsPerTick`) but stay deliberate enough to feel like
 *   typing on slow ones (`minCharsPerTick`). The scaling
 *   (`remaining / catchupDivisor`) means a 1 000-char burst paints
 *   in under a second while a trickle still types one char at a
 *   time.
 * - `prefers-reduced-motion: reduce` → snaps to full text (a11y).
 * - If `target` shrinks (buffer rewritten — CLI dedup, snapshot
 *   replay) the revealed cursor snaps back to the new end so we
 *   never render past-end characters.
 *
 * Internals: target/options are tracked in refs so the interval is
 * created once per (isStreaming, target.length === 0) edge instead
 * of every tick. The previous version had `revealed` in the effect
 * deps and re-created the interval after each setState — wasteful
 * for a hook that runs on every streaming message.
 */
export function useTypewriter(
  target: string,
  opts: {
    isStreaming: boolean;
    minCharsPerTick?: number;
    maxCharsPerTick?: number;
    catchupDivisor?: number;
    tickMs?: number;
  },
): string {
  const minCharsPerTick = opts.minCharsPerTick ?? 1;
  const maxCharsPerTick = opts.maxCharsPerTick ?? 40;
  const catchupDivisor = opts.catchupDivisor ?? 18;
  const tickMs = opts.tickMs ?? 28;
  const reducedMotion = usePrefersReducedMotion();
  const [revealed, setRevealed] = useState(opts.isStreaming && !reducedMotion ? 0 : target.length);

  // Latest target + tunables for the interval body to read without
  // forcing an effect re-run (and an interval recreate) on every
  // streaming delta.
  const targetRef = useRef(target);
  targetRef.current = target;
  const minRef = useRef(minCharsPerTick);
  minRef.current = minCharsPerTick;
  const maxRef = useRef(maxCharsPerTick);
  maxRef.current = maxCharsPerTick;
  const divisorRef = useRef(catchupDivisor);
  divisorRef.current = catchupDivisor;

  // Keep the cursor at the visible end outside animation, including
  // before a mid-stream reduced-motion preference change.
  useEffect(() => {
    setRevealed((prev) =>
      !opts.isStreaming || reducedMotion ? target.length : Math.min(prev, target.length),
    );
  }, [target, opts.isStreaming, reducedMotion]);

  useEffect(() => {
    if (!opts.isStreaming || reducedMotion) {
      setRevealed(targetRef.current.length);
      return;
    }
    const id = setInterval(() => {
      setRevealed((prev) => {
        const t = targetRef.current;
        if (prev >= t.length) return prev;
        const remaining = t.length - prev;
        const scaled = Math.ceil(remaining / divisorRef.current);
        const step = Math.max(minRef.current, Math.min(maxRef.current, scaled));
        return Math.min(prev + step, t.length);
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [opts.isStreaming, reducedMotion, tickMs]);

  return typewriterText(target, revealed, opts.isStreaming && !reducedMotion);
}

/** Only animated streams use a reveal cursor; idle updates must never be truncated. */
export function typewriterText(target: string, revealed: number, animate: boolean): string {
  return animate ? target.slice(0, revealed) : target;
}

/**
 * Live read of `prefers-reduced-motion: reduce`. We listen for
 * changes too, so users toggling OS-level motion-reduction mid-
 * session immediately stop seeing the typewriter.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}
